import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';
import ReviewsSlider from '../components/ReviewsSlider';

export default function Home() {
  const heroImage = 'https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/3c7718da-c622-4e09-b9ce-f749a5967f74/power-multiservice-hero.png';
  const privateThumb = 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/3c7718da-c622-4e09-b9ce-f749a5967f74/private-cleaning-kitchen.png';
  const commercialThumb = 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/3c7718da-c622-4e09-b9ce-f749a5967f74/commercial-office-cleaning.png';
  const calcBgImage = 'https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/3c7718da-c622-4e09-b9ce-f749a5967f74/commercial-meeting-room.png';
  const whyChooseImage = 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/3c7718da-c622-4e09-b9ce-f749a5967f74/power-multiservice-cleaning-team.png';

  return (
    <div>
      <SEO
        title="Power-Multiservice"
        description="Professionel rengøring til private og erhverv i Nørre Aaby og omegn. Få et uforpligtende tilbud på grundig og pålidelig rengøring."
        path="/"
      />

      {/* FULL WIDTH HERO SECTION WITH RIGHT-SIDE WHITE WEBFORM */}
      <section className="relative min-h-[620px] lg:min-h-[700px] flex items-center bg-slate-900 text-white overflow-hidden py-14 sm:py-20 lg:py-24">
        {/* Full-width background image */}
        <img
          src={heroImage}
          alt="Professionelt rent skandinavisk hjem i Danmark"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />

        {/* Solid uniform overlay without gradient for optimal contrast and readability */}
        <div className="absolute inset-0 bg-black/55" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            {/* Left Column: Hero Content */}
            <div className="lg:col-span-7 space-y-6 sm:space-y-8 text-center lg:text-left animate-fade-in-up">
              <h1 className="text-3xl sm:text-5xl lg:text-5xl xl:text-6xl font-black text-white tracking-tight leading-[1.12] drop-shadow-md">
                Professionel rengøring til{' '}
                <span className="text-white">private</span>{' '}
                &{' '}
                <span className="text-white">erhverv</span>
              </h1>

              <p className="text-base sm:text-lg sm:leading-relaxed text-white/95 max-w-2xl mx-auto lg:mx-0 font-normal drop-shadow-sm">
                Hos Power-Multiservice leverer vi grundig, stabil og fleksibel rengøring tilpasset dit hjem eller din arbejdsplads. Oplev en renere hverdag med fast og pålidelig service.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center lg:justify-start gap-3 sm:gap-4 pt-1">
                {/* Primary CTA */}
                <Link
                  to="/prisberegner"
                  className="group px-6 py-3.5 sm:py-4 rounded-full font-bold text-base text-slate-950 bg-emerald-400 hover:bg-emerald-300 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <i className="fa-solid fa-calculator text-slate-950 text-sm group-hover:rotate-12 transition-transform duration-300" aria-hidden="true" />
                  <span>Beregn din pris</span>
                </Link>

                {/* Secondary Call CTA */}
                <a
                  href="tel:31686764"
                  className="group px-5 py-3.5 sm:py-4 rounded-full font-semibold text-sm sm:text-base text-white bg-transparent hover:bg-transparent border border-white hover:scale-[1.03] active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap"
                  aria-label="Ring til os på 31 68 67 64"
                >
                  <i className="fa-solid fa-phone text-white text-xs group-hover:-rotate-12 transition-transform duration-200" aria-hidden="true" />
                  <span className="whitespace-nowrap">31 68 67 64</span>
                </a>
              </div>
            </div>

            {/* Right Column: White Lead Webform */}
            <div className="lg:col-span-5 w-full animate-fade-in-up delay-100">
              <div className="shadow-2xl overflow-hidden hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.35)] transition-shadow duration-300">
                <ContactForm
                  heading="Få et uforpligtende tilbud"
                  compact={true}
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* KORT INTRODUKTION MED CTA TIL AT LÆSE MERE */}
      <section className="py-16 sm:py-20 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div className="max-w-3xl space-y-3 text-left">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                En pålidelig samarbejdspartner med fokus på kvalitet og struktur
              </h2>
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                Vi leverer professionelle rengøringsløsninger med en enkel målsætning: At give dig et rent, indbydende og sundt indeklima – hvad enten det drejer sig om din private bolig eller virksomhedens arbejdsplads.
              </p>
            </div>
            <div className="shrink-0 flex items-center">
              <Link
                to="/om-os"
                className="inline-flex items-center gap-2 pb-1 font-bold text-base text-slate-900 border-b-2 border-black hover:text-emerald-700 hover:border-emerald-700 transition-colors whitespace-nowrap group"
              >
                <span>Læs mere om os</span>
                <i className="fa-solid fa-arrow-right text-xs text-slate-900 group-hover:text-emerald-700 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* YDELSER SEKTION */}
      <section className="py-16 sm:py-20 bg-slate-50 border-t border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
              Løsninger tilpasset dine specifikke behov
            </h2>
            <p className="mt-3 text-slate-600 text-base">
              Vælg dit område og læs mere om, hvad vi kan gøre for dig.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            
            {/* Card 1: Privat rengøring */}
            <div className="group bg-white overflow-hidden border border-slate-200/90 shadow-md hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="h-56 sm:h-64 overflow-hidden relative">
                  <img
                    src={privateThumb}
                    alt="Privat rengøring i moderne dansk bolig"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                  />
                </div>

                <div className="p-6 sm:p-8">
                  <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 group-hover:text-emerald-700 transition-colors">
                    Rengøring til private
                  </h3>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-6">
                    Få mere fritid og et pænt hjem med vores fleksible rengøringsaftaler. Vi hjælper med alt fra faste ugentlige besøg til grundig hovedrengøring.
                  </p>

                  <ul className="space-y-2.5 text-sm text-slate-700 mb-6">
                    <li className="flex items-center gap-2.5">
                      <i className="fa-solid fa-check text-emerald-600 text-xs shrink-0" aria-hidden="true" />
                      <span>Køkken, badeværelse og sanitære installationer</span>
                    </li>
                    <li className="flex items-center gap-2.5">
                      <i className="fa-solid fa-check text-emerald-600 text-xs shrink-0" aria-hidden="true" />
                      <span>Støvsugning, gulvvask og aftørring af støv</span>
                    </li>
                    <li className="flex items-center gap-2.5">
                      <i className="fa-solid fa-check text-emerald-600 text-xs shrink-0" aria-hidden="true" />
                      <span>Individuelt tilpasset efter dine ønsker</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="px-6 pb-6 sm:px-8 sm:pb-8 pt-0">
                <Link
                  to="/privat-rengoering"
                  className="w-full py-3.5 px-5 rounded-full font-bold text-sm text-center text-slate-900 bg-transparent hover:bg-transparent border border-slate-300 hover:border-slate-800 transition-colors flex items-center justify-center gap-2"
                >
                  <span>Læs mere om privat rengøring</span>
                  <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Card 2: Erhvervsrengøring */}
            <div className="group bg-white overflow-hidden border border-slate-200/90 shadow-md hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="h-56 sm:h-64 overflow-hidden relative">
                  <img
                    src={commercialThumb}
                    alt="Erhvervsrengøring i moderne kontor"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                  />
                </div>

                <div className="p-6 sm:p-8">
                  <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 group-hover:text-emerald-700 transition-colors">
                    Rengøring til erhverv
                  </h3>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-6">
                    Sikre et sundt indeklima for medarbejderne og et professionelt indtryk for kunderne. Vi servicerer kontorer, klinikker, butikker og fællesarealer.
                  </p>

                  <ul className="space-y-2.5 text-sm text-slate-700 mb-6">
                    <li className="flex items-center gap-2.5">
                      <i className="fa-solid fa-check text-emerald-600 text-xs shrink-0" aria-hidden="true" />
                      <span>Kontorlokaler, arbejdsstationer og møderum</span>
                    </li>
                    <li className="flex items-center gap-2.5">
                      <i className="fa-solid fa-check text-emerald-600 text-xs shrink-0" aria-hidden="true" />
                      <span>Klinikker, behandlerrum og receptionsarealer</span>
                    </li>
                    <li className="flex items-center gap-2.5">
                      <i className="fa-solid fa-check text-emerald-600 text-xs shrink-0" aria-hidden="true" />
                      <span>Stabile rutiner og skræddersyede intervaller</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="px-6 pb-6 sm:px-8 sm:pb-8 pt-0">
                <Link
                  to="/erhvervsrengoering"
                  className="w-full py-3.5 px-5 rounded-full font-bold text-sm text-center text-slate-900 bg-transparent hover:bg-transparent border border-slate-300 hover:border-slate-800 transition-colors flex items-center justify-center gap-2"
                >
                  <span>Læs mere om erhvervsrengøring</span>
                  <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* PRISBEREGNER TEASER SEKTION MED BAGGRUNDSBILLEDE */}
      <section className="py-16 sm:py-24 relative text-white overflow-hidden">
        {/* Baggrundsbillede */}
        <img
          src={calcBgImage}
          alt="Professionelt erhvervslokale i Danmark"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Mørk overlay for optimal kontrast */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-emerald-950/85 to-slate-950/90" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
            <div className="max-w-2xl text-left space-y-4">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white">
                Hvad koster professionel rengøring?
              </h2>

              <p className="text-slate-200 text-base sm:text-lg leading-relaxed">
                Brug vores online prisberegner og få straks en vejledende prisindikation baseret på dine kvadratmeter og ønsker. Du kan efterfølgende sende oplysningerne direkte til os for et endeligt tilbud.
              </p>
            </div>

            <div className="shrink-0 flex flex-col sm:flex-row items-center gap-4 w-full lg:w-auto">
              <Link
                to="/prisberegner"
                className="w-full sm:w-auto px-8 py-4 rounded-full font-bold text-base text-slate-950 bg-emerald-400 hover:bg-emerald-300 active:scale-[0.98] transition-all flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="fa-solid fa-calculator text-sm" aria-hidden="true" />
                <span>Beregn din pris</span>
              </Link>

              <Link
                to="/kontakt"
                className="w-full sm:w-auto px-6 py-4 rounded-full font-bold text-sm text-white bg-transparent hover:bg-transparent border border-white transition-all flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <span>Kontakt os</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* HVORFOR VÆLGE POWER-MULTISERVICE (BILLEDE I VENSTRE & TEKST I HØJRE) */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            
            {/* Venstre side: Billede */}
            <div className="lg:col-span-5 order-2 lg:order-1 animate-fade-in-up delay-100">
              <div className="group overflow-hidden aspect-[4/3] lg:aspect-[5/4]">
                <img
                  src={whyChooseImage}
                  alt="Professionelt rengøringsudstyr og grundig service hos Power-Multiservice"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                />
              </div>
            </div>

            {/* Højre side: Tekst & fordele */}
            <div className="lg:col-span-7 space-y-8 text-left order-1 lg:order-2">
              <div className="space-y-4">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
                  Hvorfor vælge Power-Multiservice?
                </h2>
                <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
                  Kort og godt: Vi leverer et grundigt stykke arbejde og holder, hvad vi lover. Uanset om det gælder den ugentlige rengøring i privaten eller faste erhvervsaftaler, er vi din trygge partner med fokus på orden, grundighed og høj kundetilfredshed.
                </p>
              </div>

              {/* Fordele grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-2">
                
                {/* Punkt 1 */}
                <div className="flex items-center gap-3 group/item hover:translate-x-1 transition-transform duration-200 cursor-default">
                  <div className="text-black text-2xl shrink-0 w-8 flex justify-center group-hover/item:text-emerald-600 transition-colors">
                    <i className="fa-solid fa-sliders" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 group-hover/item:text-emerald-800 transition-colors">Fleksible løsninger</h3>
                </div>

                {/* Punkt 2 */}
                <div className="flex items-center gap-3 group/item hover:translate-x-1 transition-transform duration-200 cursor-default">
                  <div className="text-black text-2xl shrink-0 w-8 flex justify-center group-hover/item:text-emerald-600 transition-colors">
                    <i className="fa-solid fa-sparkles" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 group-hover/item:text-emerald-800 transition-colors">Fokus på grundighed</h3>
                </div>

                {/* Punkt 3 */}
                <div className="flex items-center gap-3 group/item hover:translate-x-1 transition-transform duration-200 cursor-default">
                  <div className="text-black text-2xl shrink-0 w-8 flex justify-center group-hover/item:text-emerald-600 transition-colors">
                    <i className="fa-solid fa-user-check" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 group-hover/item:text-emerald-800 transition-colors">Personlig kontakt</h3>
                </div>

                {/* Punkt 4 */}
                <div className="flex items-center gap-3 group/item hover:translate-x-1 transition-transform duration-200 cursor-default">
                  <div className="text-black text-2xl shrink-0 w-8 flex justify-center group-hover/item:text-emerald-600 transition-colors">
                    <i className="fa-solid fa-file-invoice" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 group-hover/item:text-emerald-800 transition-colors">Nem tilbudsproces</h3>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* KUNDEUDTALELSER (RENDERES HVIS DER ER ANMELDELSER I CMS) */}
      <ReviewsSlider />

      {/* AFSLUTTENDE CTA SEKTION (KOMPAKT UDEN BAGGRUND) */}
      <section className="py-10 sm:py-12 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 lg:gap-10">
            
            {/* Venstrestillet tekst */}
            <div className="max-w-2xl space-y-2 text-left">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-slate-950 tracking-tight">
                Klar til et skinnende rent resultat?
              </h2>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                Kontakt Power-Multiservice i dag til en uforpligtende snak om dine rengøringsbehov, eller prøv vores hurtige online prisberegner.
              </p>
            </div>

            {/* CTA i højre side */}
            <div className="shrink-0 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link
                to="/kontakt"
                className="px-6 py-3.5 rounded-full font-bold text-sm sm:text-base text-white bg-emerald-600 hover:bg-emerald-500 active:scale-[0.98] transition-all flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <span>Kontakt os for et tilbud</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>

              <Link
                to="/prisberegner"
                className="px-5 py-3.5 rounded-full font-bold text-sm sm:text-base text-black bg-transparent hover:bg-transparent border border-black active:scale-[0.98] transition-all flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="fa-solid fa-calculator text-black text-sm" aria-hidden="true" />
                <span>Prøv prisberegneren</span>
              </Link>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
